import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Users, Plus, Mail, Building } from "lucide-react";
import { isStaffEmail } from "@/lib/auth-domain";
import {
  getOrCreateTeamForCurrentCustomer,
  listTeamMembers,
  inviteMember,
  type Team,
  type TeamMember,
} from "@/lib/teams";

export const Route = createFileRoute("/app/customer/team")({ component: TeamPage });

function TeamPage() {
  const { profile } = useAuth();
  const [team, setTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [members, setMembers] = useState<TeamMember[]>([]);

  // Redirect non-customer users
  if (!profile || profile.role !== "customer") {
    redirect({ to: "/app/customer/dashboard" });
  }

  const domain = profile?.email?.split("@")[1] ?? "";

  // Prevent staff domains from creating teams
  if (isStaffEmail(profile?.email ?? "")) {
    return (
      <div className="text-center py-20">
        <h1 className="text-2xl font-bold">Team access is for customers only</h1>
        <p className="mt-2 text-muted-foreground">Staff accounts do not require team access.</p>
      </div>
    );
  }

  // Load existing team on mount
  useEffect(() => {
    let cancelled = false;
    async function loadTeam() {
      if (!profile?.id) return;
      setLoading(true);
      try {
        // Try to find existing team without creating one
        const { supabase } = await import("@/integrations/supabase/client");
        const { data: existing } = await (supabase as any)
          .from("customer_teams")
          .select("*")
          .eq("owner_profile_id", profile.id)
          .maybeSingle();
        if (cancelled) return;
        if (existing) {
          setTeam(existing as Team);
          const m = await listTeamMembers(existing.id);
          if (!cancelled) setMembers(m);
        }
      } catch (e) {
        console.error("Failed to load team:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadTeam();
    return () => {
      cancelled = true;
    };
  }, [profile?.id]);

  const handleCreateTeam = async () => {
    if (!profile?.id || !profile.email) return;
    setCreating(true);
    try {
      const t = await getOrCreateTeamForCurrentCustomer(profile.id, profile.email);
      if (!t) {
        toast.error("Could not create team. Please try again.");
        return;
      }
      setTeam(t);
      const m = await listTeamMembers(t.id);
      setMembers(m);
      toast.success(`Team created for ${domain}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to create team");
    } finally {
      setCreating(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail || !team) return;
    setInviting(true);
    try {
      await inviteMember(team.id, inviteEmail, domain);
      setInviteEmail("");
      const m = await listTeamMembers(team.id);
      setMembers(m);
      toast.success(`Invite sent to ${inviteEmail}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to invite");
    } finally {
      setInviting(false);
    }
  };

  if (loading) {
    return <div className="py-20 text-center text-muted-foreground">Loading team…</div>;
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Team access for your company</h1>
        <p className="mt-2 text-muted-foreground">
          Invite colleagues from your company to collaborate on FlightDeck.
        </p>
      </div>

      {/* Team domain badge */}
      <div className="inline-flex items-center gap-2 rounded-full bg-muted px-4 py-2 text-sm">
        <Building className="h-4 w-4" />
        Team domain: <span className="font-semibold">{domain}</span>
      </div>

      {!team ? (
        <div className="rounded-3xl border border-border bg-card p-8 shadow-soft">
          <div className="flex items-start gap-4">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-soft text-primary">
              <Users className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold">Create your team</h2>
              <p className="mt-2 text-muted-foreground">
                FlightDeck lets you invite colleagues from your company so they can upload artwork
                and track jobs too.
              </p>
              <Button
                onClick={handleCreateTeam}
                disabled={creating}
                className="mt-4 rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
              >
                {creating ? "Creating…" : `Create team for ${domain}`}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Invite form */}
          <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Plus className="h-5 w-5" /> Invite teammate
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Invite teammates using their @{domain} email.
            </p>
            <div className="mt-4 flex gap-2">
              <div className="flex-1">
                <Label htmlFor="invite-email" className="sr-only">
                  Email
                </Label>
                <Input
                  id="invite-email"
                  type="email"
                  placeholder={`colleague@${domain}`}
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="rounded-2xl"
                />
              </div>
              <Button
                onClick={handleInvite}
                disabled={inviting || !inviteEmail}
                className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
              >
                <Mail className="h-4 w-4 mr-2" /> {inviting ? "Inviting…" : "Invite"}
              </Button>
            </div>
          </div>

          {/* Team members */}
          <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Users className="h-5 w-5" /> Team members
            </h2>
            <div className="mt-4 space-y-3">
              {members.length === 0 && (
                <p className="text-sm text-muted-foreground">No members yet.</p>
              )}
              {members.map((member) => (
                <div
                  key={member.profile_id}
                  className="flex items-center justify-between rounded-2xl border border-border bg-muted/30 p-4"
                >
                  <div className="flex items-center gap-3">
                    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gradient-hero text-primary-foreground text-sm font-bold">
                      {(member.full_name ?? member.email ?? "?").slice(0, 1).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-medium">{member.full_name ?? "Teammate"}</div>
                      <div className="text-sm text-muted-foreground">{member.email}</div>
                    </div>
                  </div>
                  <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary capitalize">
                    {member.role}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
